// Module `collab`: collaborations ("samenwerkingsverbanden") and the quarterly
// workload statements (docs/superpowers/plans/2026-10-11-samenwerkingsverbanden.md).
// The screens say settings.collabLabel / collabLabelPlural, never "collab".
//
// Days: daysPerWeek is what a statement line says (0-7, steps of 0.5).
// "Quarter days" = daysPerWeek × weeksPerQuarter (budgets are per quarter).
// Money: integer euro cents.
//
// Act-as: the statement and confirmation routes take ?as=<employeeId> (owner
// or admin, only when WorkforceSettings.actAsEnabled).
import { z } from "zod";
import { uuid } from "../schemas/common";
import { isoDate, text, textOrEmpty, type Id } from "./common";
import type { WorkloadColour } from "./workforce";

// ---------- building blocks ----------

export const ATTESTATION_ROUND_STATUSES = ["open", "closed"] as const;
export type AttestationRoundStatus = (typeof ATTESTATION_ROUND_STATUSES)[number];
export const ATTESTATION_STATUSES = ["draft", "submitted"] as const;
export type AttestationStatus = (typeof ATTESTATION_STATUSES)[number];
export const ATTESTATION_LINE_STATUSES = ["pending", "confirmed", "questioned"] as const;
export type AttestationLineStatus = (typeof ATTESTATION_LINE_STATUSES)[number];
export const ATTESTATION_LINE_STATUS_LABELS: Record<AttestationLineStatus, string> = {
  pending: "Not confirmed yet",
  confirmed: "Confirmed",
  questioned: "Questioned",
};

/** Days per week on one line: 0-7 in steps of 0.5. */
export const daysPerWeek = z
  .number({ invalid_type_error: "Enter a number of days." })
  .min(0, "Cannot be negative.")
  .max(7, "At most 7 days a week.")
  .refine((n) => Number.isInteger(n * 2), "Use steps of half a day.");

const isoDateOrNull = z
  .union([isoDate, z.literal("")])
  .nullable()
  .transform((v) => (v === "" ? null : v));
const centsOrNull = z.number().int("Whole cents.").min(0, "Cannot be negative.").max(100_000_000_000, "The amount is too large.").nullable();
const quarterDaysOrNull = z
  .number()
  .min(0, "Cannot be negative.")
  .max(99_999, "Too many days.")
  .refine((n) => Math.abs(Math.round(n * 10) - n * 10) < 1e-9, "Use at most one decimal.")
  .nullable();

/** A round in a picker or a heading: "Q1 2027". */
export type RoundRef = { id: Id; label: string; year: number; quarter: number; status: AttestationRoundStatus };

/** The owner as shown: the employee, or (when they left or none is set) the sponsoring unit's manager. */
export type CollabOwner = {
  employeeId: Id | null;
  /** "Jan van der Berg"; null when nobody (no owner and no manager). */
  name: string | null;
  /** true: the owner left or is not set; the sponsoring unit's manager is shown instead. */
  isFallback: boolean;
};

// ---------- kinds: GET/PUT /api/collab-kinds ----------

export type CollabKind = {
  id: Id;
  name: string;
  sortOrder: number;
  /** The kind of the teams' own caseloads (exactly one per org). */
  isCaseload: boolean;
  collabCount: number;
};

/** PUT replaces the list in this order; id null = new, existing ids kept; a kind in use cannot be removed (409). */
export const collabKindsBody = z.object({
  kinds: z
    .array(z.object({ id: uuid.nullable(), name: text(60, "Name"), isCaseload: z.boolean() }))
    .min(1, "Add at least one kind.")
    .max(30)
    .refine((ks) => ks.filter((k) => k.isCaseload).length === 1, "Mark exactly one kind as the teams' caseload.")
    .refine((ks) => new Set(ks.map((k) => k.name.toLowerCase())).size === ks.length, "Each kind needs its own name."),
});
export type CollabKindsBody = z.infer<typeof collabKindsBody>;

// ---------- collabs: /api/collabs ----------

/** The list's sortable columns (GET /api/collabs?sort=). */
export const COLLAB_SORTS = ["name", "kind", "owner", "members", "days", "budgetUse"] as const;
export type CollabSort = (typeof COLLAB_SORTS)[number];
/** Rows per page of GET /api/collabs. */
export const COLLAB_PAGE_SIZE = 50;

/** GET /api/collabs?kindId=&ownerId=&unitId=&q=&archived=&page=&sort=&dir= */
export const collabsQuery = z.object({
  kindId: uuid.optional(),
  /** An employee id. */
  ownerId: uuid.optional(),
  /** The sponsoring unit (and below). */
  unitId: uuid.optional(),
  q: z.string().trim().max(100).optional(),
  /** "1": archived ones too. */
  archived: z.enum(["0", "1"]).optional(),
  /** 1-based; past the last page gives the last page. */
  page: z.coerce.number().int().min(1).max(10_000).default(1),
  /** Default name. Owner sorts by the owner's surname (prefixes such as "van der" left out). */
  sort: z.enum(COLLAB_SORTS).default("name"),
  dir: z.enum(["asc", "desc"]).default("asc"),
});
export type CollabsQuery = z.infer<typeof collabsQuery>;

export type CollabListItem = {
  id: Id;
  name: string;
  code: string;
  kindId: Id;
  kindName: string;
  isCaseload: boolean;
  owner: CollabOwner;
  sponsorUnitId: Id;
  sponsorUnitName: string;
  /** Set on a team's caseload. */
  teamUnitId: Id | null;
  /** People with a line on it in the current round (the open one, else the latest). */
  members: number;
  /** Distinct top-level units (directies) those people come from. */
  departments: number;
  /** Σ days per week of those lines (submitted statements). */
  daysThisRound: number;
  budgetDaysPerQuarter: number | null;
  /** Null too when the caller may not see the money (canSeeMoney). */
  budgetCentsPerQuarter: number | null;
  startDate: string;
  endDate: string | null;
  archived: boolean;
  /** Owner/admin, the owner, or a manager of the sponsoring unit (or above): sees the money. */
  canSeeMoney: boolean;
  /** daysThisRound × weeks per quarter, one decimal: the effort in days per quarter (the list's one unit). */
  daysThisQuarter: number;
  /** daysThisQuarter / budgetDaysPerQuarter as a whole percentage; null without a days budget (or a budget of 0). */
  budgetUsePct: number | null;
};

/** GET /api/collabs: one page of the list. */
export type CollabPage = {
  items: CollabListItem[];
  /** Rows matching the filters, over all pages. */
  total: number;
  page: number;
  pageSize: number;
  /** The round the members and days are counted in (the open one, else the latest); null before the first. */
  round: RoundRef | null;
};

/** POST /api/collabs, PATCH /api/collabs/:collabId */
export type Collab = {
  id: Id;
  kindId: Id;
  kindName: string;
  isCaseload: boolean;
  name: string;
  code: string;
  description: string;
  owner: CollabOwner;
  sponsorUnitId: Id;
  sponsorUnitName: string;
  teamUnitId: Id | null;
  startDate: string;
  endDate: string | null;
  budgetDaysPerQuarter: number | null;
  budgetCentsPerQuarter: number | null;
  archivedAt: string | null;
};

export const collabBody = z
  .object({
    kindId: uuid,
    name: text(200, "Name"),
    code: textOrEmpty(30, "Code").default(""),
    description: textOrEmpty(5000, "Description").default(""),
    /** null only while unassigned. */
    ownerEmployeeId: uuid.nullable(),
    sponsorUnitId: uuid,
    startDate: isoDate,
    endDate: isoDateOrNull.default(null),
    budgetDaysPerQuarter: quarterDaysOrNull.default(null),
    /** Personnel plus other costs. */
    budgetCentsPerQuarter: centsOrNull.default(null),
  })
  .refine((v) => !v.endDate || v.endDate >= v.startDate, { message: "The end date cannot be before the start date.", path: ["endDate"] });
export type CollabBody = z.infer<typeof collabBody>;

export const collabPatch = z
  .object({
    kindId: uuid,
    name: text(200, "Name"),
    code: textOrEmpty(30, "Code"),
    description: textOrEmpty(5000, "Description"),
    ownerEmployeeId: uuid.nullable(),
    sponsorUnitId: uuid,
    startDate: isoDate,
    endDate: isoDateOrNull,
    budgetDaysPerQuarter: quarterDaysOrNull,
    budgetCentsPerQuarter: centsOrNull,
    /** true archives, false restores. */
    archived: z.boolean(),
  })
  .partial()
  .refine((v) => Object.keys(v).length > 0, "Nothing to change.")
  .refine((v) => !v.endDate || !v.startDate || v.endDate >= v.startDate, { message: "The end date cannot be before the start date.", path: ["endDate"] });
export type CollabPatch = z.infer<typeof collabPatch>;
export const collabParams = z.object({ collabId: uuid });
/** ?roundId= on the detail and the report: default the open round, else the latest. */
export const collabRoundQuery = z.object({ roundId: uuid.optional() });

/** POST /api/collabs/sync-caseloads */
export type SyncCaseloadsResult = { created: number };

/** One person on a collab in a round (detail and report). */
export type CollabMember = {
  lineId: Id;
  employeeId: Id;
  /** "First Last", as displayName. */
  name: string;
  /** "First Last" ("Daan de Boer"); members are sorted by surname without the prefix. */
  displayName: string;
  jobTitle: string;
  homeUnitId: Id;
  homeUnitName: string;
  /** The top-level unit (directie) above the home unit. */
  departmentId: Id | null;
  departmentName: string | null;
  /**
   * Days, line status and statement status: null unless the caller has the
   * full view of the collab, is the person, or manages the person's (frozen)
   * home unit or a unit above it.
   */
  daysPerWeek: number | null;
  /** The employee's note: only for the person, the collab's owner and owner/admin; null otherwise. */
  note: string | null;
  status: AttestationLineStatus | null;
  /** As note. */
  ownerComment: string | null;
  /** A draft statement does not count in the actuals yet. */
  statementStatus: AttestationStatus | null;
};

/** Budget against actuals for one round. Unconfirmed lines count, flagged. */
export type CollabActuals = {
  round: RoundRef;
  budgetDays: number | null;
  /** Σ daysPerWeek × weeksPerQuarter of the lines of submitted statements. */
  actualDays: number;
  /** Of actualDays: lines not confirmed yet ("not confirmed"). */
  unconfirmedDays: number;
  budgetCents: number | null;
  /** Σ line personnel cost (calc lineCostCents). The money fields are null without canSeeMoney. */
  personnelCents: number | null;
  /** collab_cost rows with spent_on inside the round's quarter. */
  otherCents: number | null;
  /** personnelCents + otherCents */
  totalCents: number | null;
  /** totalCents > budgetCents, or actualDays > budgetDays (only the days without canSeeMoney). */
  overBudget: boolean;
};

/** GET /api/collabs/:collabId?roundId= */
export type CollabDetail = {
  collab: Collab;
  /** The round shown (null: no round yet). */
  round: RoundRef | null;
  /** Every round, newest first (the round picker). */
  rounds: RoundRef[];
  members: CollabMember[];
  /** Distinct departments of the members. */
  departments: number;
  /** Per round, newest first. */
  actuals: CollabActuals[];
  /** Lines of the shown round still pending / questioned. */
  pendingCount: number;
  questionedCount: number;
  /** Other costs, newest first. */
  costs: CollabCost[];
  /** Owner/admin: edit, costs. */
  canEdit: boolean;
  /** The caller owns it (or acts as the owner): confirm lines. */
  canConfirm: boolean;
  /** Full view: every member's days and line status (owner/admin, the owner, managers of the sponsoring unit or above). */
  canSeeDetails: boolean;
  /** The money (costs, budget in euros): as canSeeDetails. */
  canSeeMoney: boolean;
};

// ---------- other costs ----------

export type CollabCost = {
  id: Id;
  collabId: Id;
  spentOn: string;
  /** Null without canSeeMoney. */
  amountCents: number | null;
  description: string;
  supplier: string;
  createdByName: string | null;
  createdAt: string;
};

/** POST /api/collabs/:collabId/costs */
export const collabCostBody = z.object({
  spentOn: isoDate,
  amountCents: z.number().int("Whole cents.").min(0, "Cannot be negative.").max(100_000_000_000, "The amount is too large."),
  description: text(300, "Description"),
  supplier: textOrEmpty(200, "Supplier").default(""),
});
export type CollabCostBody = z.infer<typeof collabCostBody>;
export const collabCostParams = z.object({ costId: uuid });

// ---------- the management report: GET /api/collabs/:collabId/report?roundId= (and report.pdf) ----------

export type CollabReport = {
  collab: Collab;
  round: RoundRef;
  /** ISO timestamp. */
  generatedAt: string;
  members: CollabMember[];
  effort: {
    people: number;
    departments: number;
    daysPerWeek: number;
    /** daysPerWeek / 5 */
    fte: number;
    quarterDays: number;
    byDepartment: { departmentId: Id | null; departmentName: string; people: number; daysPerWeek: number }[];
  };
  /** The round's budget against actuals. */
  cost: CollabActuals;
  /** Up to the last 4 rounds, oldest first (includes the round itself). */
  trend: CollabActuals[];
  /** Lines in the round not confirmed yet. */
  notConfirmedCount: number;
  /** As on the detail. */
  canSeeDetails: boolean;
  canSeeMoney: boolean;
};

// ---------- rounds (quarters): /api/attestation-rounds ----------

export type AttestationRound = {
  id: Id;
  year: number;
  quarter: number;
  /** "Q1 2027" */
  label: string;
  opensOn: string;
  dueOn: string;
  status: AttestationRoundStatus;
  closedAt: string | null;
  /** Statements in the round. */
  statements: number;
  submitted: number;
  /** Drafts (in a closed round: "not received"). */
  drafts: number;
  linesPending: number;
  linesQuestioned: number;
  /** Employees with a statement and an email (they get the link and reminders). */
  wouldEmail: number;
  /** Employees with a statement and no email (they get nothing). */
  noEmail: number;
};

/** POST /api/attestation-rounds: at most one open round (409). */
export const attestationRoundBody = z
  .object({
    year: z.number().int().min(2000).max(2100),
    quarter: z.number().int().min(1).max(4),
    opensOn: isoDate,
    dueOn: isoDate,
  })
  .refine((v) => v.dueOn >= v.opensOn, { message: "The due date cannot be before the opening date.", path: ["dueOn"] });
export type AttestationRoundBody = z.infer<typeof attestationRoundBody>;
/** Plan name. */
export const roundBody = attestationRoundBody;
export const roundParams = z.object({ roundId: uuid });

// ---------- my quarter: /api/attestations ----------

/** GET /api/attestations/mine?roundId=&as= (default: the open round, else the latest). */
export const myStatementQuery = z.object({ roundId: uuid.optional(), as: uuid.optional() });

export type MyStatementLine = {
  id: Id;
  collabId: Id;
  collabName: string;
  kindName: string;
  ownerName: string | null;
  daysPerWeek: number;
  note: string;
  status: AttestationLineStatus;
  /** The owner's comment (shown when questioned). */
  ownerComment: string;
  decidedAt: string | null;
};

/** A collab the employee can add a line for (active, not archived, not ended). */
export type CollabOption = { id: Id; name: string; code: string; kindName: string; sponsorUnitName: string; isCaseload: boolean };

export type MyStatement = {
  attestationId: Id;
  round: RoundRef & { opensOn: string; dueOn: string };
  employee: { id: Id; name: string; jobTitle: string; homeUnitName: string };
  status: AttestationStatus;
  submittedAt: string | null;
  /** Who pressed submit, when it was someone acting as the employee. */
  submittedByName: string | null;
  /** Frozen on the statement. */
  contractHours: number;
  hoursPerDay: number;
  /** contractHours / hoursPerDay */
  contractDaysPerWeek: number;
  lines: MyStatementLine[];
  totalDaysPerWeek: number;
  workloadPct: number;
  workloadColour: WorkloadColour;
  /** Collabs to pick from for a new line. */
  options: CollabOption[];
  /** The round is closed: no changes (writes answer 409). */
  readOnly: boolean;
  /** Set after a save or submit above 100%: "Your week adds up to 112%." */
  warning: string | null;
  /** Set when an owner/admin acts as the employee. */
  actingAs: { employeeId: Id; name: string } | null;
};

/** PUT /api/attestations/:attestationId?as= (lines replace the statement's lines). */
export const statementBody = z.object({
  lines: z
    .array(z.object({ collabId: uuid, daysPerWeek, note: textOrEmpty(1000, "Note").default("") }))
    .max(50)
    .refine((ls) => new Set(ls.map((l) => l.collabId)).size === ls.length, "Each line needs its own collaboration."),
});
export type StatementBody = z.infer<typeof statementBody>;
export const attestationParams = z.object({ attestationId: uuid });

// ---------- to confirm: /api/confirmations, /api/attestation-lines ----------

/**
 * A pending or questioned line of a submitted statement that the caller
 * decides on: on a collab they own, or, for the owner's own line, the
 * manager of the sponsoring unit (or the next unit up; else an owner/admin).
 */
export type ConfirmationItem = {
  lineId: Id;
  collabId: Id;
  collabName: string;
  roundId: Id;
  roundLabel: string;
  employeeId: Id;
  employeeName: string;
  jobTitle: string;
  homeUnitName: string;
  daysPerWeek: number;
  /** The employee's note. */
  note: string;
  status: AttestationLineStatus;
  ownerComment: string;
  decidedAt: string | null;
  statementStatus: AttestationStatus;
  /** Why the line is the caller's to decide (the "Why you" on To confirm). */
  reason: ConfirmationReason;
};

/** POST /api/attestation-lines/:lineId/question?as= (returns the line to the employee). */
export const attestationQuestionBody = z.object({ comment: text(2000, "Your question") });
export type AttestationQuestionBody = z.infer<typeof attestationQuestionBody>;
export const attestationLineParams = z.object({ lineId: uuid });

/** POST /api/confirmations/confirm-all?as= : every pending line the caller owns, or one collab's. */
export const confirmAllBody = z.object({ collabId: uuid.optional() });
export type ConfirmAllBody = z.infer<typeof confirmAllBody>;
export type ConfirmAllResult = { confirmed: number };

// ---------- workload: /api/workload ----------

/** GET /api/workload?roundId=&unitId= ; /api/workload/overloaded?roundId= ; /api/workload/export.xlsx?roundId= */
export const collabWorkloadQuery = z.object({ roundId: uuid.optional(), unitId: uuid.optional() });
export type CollabWorkloadQuery = z.infer<typeof collabWorkloadQuery>;

/** One unit in one round. */
export type WorkloadCell = {
  roundId: Id;
  /** Average workload % of submitted statements; null when none. */
  averagePct: number | null;
  colour: WorkloadColour | null;
  green: number;
  amber: number;
  red: number;
  /** Drafts (no statement submitted). */
  notReceived: number;
  /** The round is still open: the figures are provisional, not final. */
  provisional: boolean;
};

export type WorkloadUnitRow = {
  unitId: Id;
  name: string;
  kindName: string;
  isTeam: boolean;
  /** Active people in the unit and below. */
  headcount: number;
  /** Has sub-units (drill-down). */
  hasChildren: boolean;
  /** One per WorkloadView.rounds, same order. */
  cells: WorkloadCell[];
};

export type WorkloadPersonCell = {
  roundId: Id;
  workloadPct: number | null;
  colour: WorkloadColour | null;
  /** none: no statement in that round. */
  status: AttestationStatus | "none";
  /** The round is still open: the figure is provisional, not final. */
  provisional: boolean;
};

export type WorkloadPersonRow = {
  employeeId: Id;
  name: string;
  jobTitle: string;
  /** One per WorkloadView.rounds, same order. */
  cells: WorkloadPersonCell[];
};

export type WorkloadView = {
  /** The round asked for (default the open one, else the latest); null without rounds. */
  round: RoundRef | null;
  /** The heat map's columns: up to the last 4 rounds ending with `round`, oldest first. */
  rounds: RoundRef[];
  /** The unit drilled into; null = the top (departments, or what the caller manages). */
  unit: { id: Id; name: string; kindName: string; isTeam: boolean } | null;
  /** From the top down to the unit's parent (drill-up). */
  ancestors: { id: Id; name: string }[];
  /** Child units of `unit` (or the top-level units the caller may see). */
  rows: WorkloadUnitRow[];
  /** The people of `unit` when it is a team; else null. */
  people: WorkloadPersonRow[] | null;
  thresholds: { amberPct: number; redPct: number };
};

/** GET /api/workload/overloaded: above amberPct, highest first. */
export type OverloadedPerson = {
  employeeId: Id;
  name: string;
  jobTitle: string;
  homeUnitId: Id;
  homeUnitName: string;
  departmentName: string | null;
  workloadPct: number;
  colour: WorkloadColour;
  totalDaysPerWeek: number;
  contractHours: number;
  /** Lines on the statement. */
  collabs: number;
};

// ---------- request input types (what the web sends: defaults may be left out) ----------

export type CollabKindsInput = z.input<typeof collabKindsBody>;
export type CollabsQueryInput = z.input<typeof collabsQuery>;
export type CollabInput = z.input<typeof collabBody>;
export type CollabPatchInput = z.input<typeof collabPatch>;
export type CollabCostInput = z.input<typeof collabCostBody>;
export type AttestationRoundInput = z.input<typeof attestationRoundBody>;
export type StatementInput = z.input<typeof statementBody>;
export type AttestationQuestionInput = z.input<typeof attestationQuestionBody>;
export type ConfirmAllInput = z.input<typeof confirmAllBody>;

// ---------- To confirm: why a line is yours (UX consolidation, My tasks) ----------

/**
 * Why a line reaches the person deciding on it: they own the collab; the
 * owner's own line (or a collab without an active owner) goes to the manager
 * of the sponsoring unit, else to the manager of the nearest unit above; with
 * nobody left in the tree, to an owner or administrator of the organisation.
 */
export const CONFIRMATION_REASONS = ["owner", "sponsor_manager", "ancestor_manager", "admin"] as const;
export type ConfirmationReason = (typeof CONFIRMATION_REASONS)[number];

// ---------- The explorer: "Where the work flows" (docs/superpowers/plans/2026-10-11-collab-explorer.md) ----------

export const EXPLORER_FOCUS_KINDS = ["unit", "collab", "org"] as const;
export type ExplorerFocusKind = (typeof EXPLORER_FOCUS_KINDS)[number];

/** Node ids that are not a unit or collab id. */
export const EXPLORER_OTHER = "other";
/** Every team caseload as one node (unit mode above the teams, and the organisation). */
export const EXPLORER_CASELOADS = "caseloads";
/** The people homed in the focus unit itself (not in one of its child units): `direct:<unitId>`. */
export const explorerDirectId = (unitId: string): string => `direct:${unitId}`;

/** GET /collab-explorer: focus and id left out = the caller's own starting point. */
export const collabExplorerQuery = z.object({
  focus: z.enum(EXPLORER_FOCUS_KINDS).optional(),
  id: uuid.optional(),
  roundId: uuid.optional(),
});
export type CollabExplorerQuery = z.infer<typeof collabExplorerQuery>;
export type CollabExplorerQueryInput = z.input<typeof collabExplorerQuery>;

const explorerNodeId = z.string().trim().min(1).max(80);

/**
 * GET /collab-explorer/link: one ribbon. `from` and `to` are node ids of the
 * view the ribbon came from; pass that view's `focus` and `id` too, so
 * "other", "caseloads" and `direct:` resolve the same way (without them a
 * unit id means its whole subtree and a collab id the one collab).
 */
export const collabExplorerLinkQuery = z.object({
  from: explorerNodeId,
  to: explorerNodeId,
  roundId: uuid.optional(),
  focus: z.enum(EXPLORER_FOCUS_KINDS).optional(),
  id: uuid.optional(),
});
export type CollabExplorerLinkQuery = z.infer<typeof collabExplorerLinkQuery>;
export type CollabExplorerLinkQueryInput = z.input<typeof collabExplorerLinkQuery>;

export type ExplorerNode = {
  /** Unit id, collab id, "other", "caseloads" or `direct:<unitId>`. */
  id: string;
  kind: "unit" | "collab" | "other";
  name: string;
  /** Unit kind + manager, or collab kind + owner. */
  subtitle: string;
  /** Total in this view of what the caller may see; null when every ribbon to it is hidden. */
  days: number | null;
  people: number | null;
  /** Collab node: sponsored outside the left node's directie (right side only; true if any ribbon to it is elsewhere). */
  elsewhere: boolean;
  isCaseload: boolean;
  /** The unit can be zoomed into. */
  hasChildren: boolean;
  /** Web route. */
  href: string;
};

export type ExplorerLink = {
  /** Node ids. */
  from: string;
  to: string;
  /** Null = hidden (fewer than 5 submitted statements behind it). */
  days: number | null;
  people: number | null;
  elsewhere: boolean;
  hidden: boolean;
};

export type ExplorerRound = { id: string; label: string; open: boolean };

export type ExplorerView = {
  focus: { kind: ExplorerFocusKind; id: string | null; name: string };
  /** Zoom breadcrumb, top first (the organisation first, id "org"). */
  path: { id: string; name: string }[];
  /** Null only while the organisation has no quarter yet (everything else is then empty). */
  round: ExplorerRound | null;
  /** For the chips, oldest first: the closed quarters of the last year plus the open one. */
  rounds: ExplorerRound[];
  left: ExplorerNode[];
  right: ExplorerNode[];
  links: ExplorerLink[];
  /**
   * What the caller may see. `directies`: unit and organisation mode, the
   * directies sponsoring the work on the right; collab mode, the directies
   * the days came from. `partial`: some ribbons are hidden, so the totals
   * leave them out.
   */
  totals: { days: number | null; elsewhereDays: number | null; elsewherePct: number | null; directies: number; partial: boolean };
  /** This is the caller's own starting point. */
  isDefault: boolean;
};

export type ExplorerLinkPerson = { employeeId: string; name: string; days: number; unitName: string };

/** GET /collab-explorer/link: people null when the caller may not see them (or the ribbon is hidden). */
export type ExplorerLinkDetail = {
  from: string;
  to: string;
  days: number | null;
  people: ExplorerLinkPerson[] | null;
};
